import React, { useEffect, useState } from 'react';
import { Table, Input, Button, message, Select, Tag, Tooltip } from 'antd';
import { FilterOutlined, CloseCircleOutlined } from '@ant-design/icons';
import FilterUsersDrawer from './FilterUsersDrawer';
import Navbar from 'src/common/Navbar/Navbar';
import GenericHoc from 'src/common/generic-hoc';
import { HocOptions } from 'src/common/generic-hoc.types';
import { UserManagementStyles } from './UserManagement.styles';
import AddUserDrawer from './AddUserDrawer';
import UserDetailsModal from './UserDetailsModal';
import SimplePaginator from 'src/common/SimplePaginator';
import {
  downloadVendorUsers,
  fetchVendorUsers,
  resolveUserHubs,
  HubRef,
} from './UserManagement.api';

interface UserRecord {
  key: string;
  id: string;
  serialNumber: number;
  userCode: string;
  name: string;
  phoneNumber: string;
  city: string;
  loginUsername: string;
  status: string;
  creationDate: string;
  lastUpdated: string;
  roles: string[];
  hubs: HubRef[];
  hubIds: string[];
}

const buildVendorUserParams = (
  username: string,
  filterValues: Record<string, any> = {},
  page: number,
  size: number,
) => {
  const params: Record<string, any> = {
    page,
    pageNum: page,
    pageSize: size,
  };

  if (username) {
    params.username = username;
  }

  if (filterValues?.phone) {
    params.phone = filterValues.phone;
  }

  if (filterValues?.city) {
    params.city = filterValues.city;
  }

  if (filterValues?.status) {
    params.status = filterValues.status;
  }

  return params;
};

const getFileNameFromContentDisposition = (header?: string): string => {
  if (!header) {
    return '';
  }

  const fileNameStarMatch = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (fileNameStarMatch?.[1]) {
    return decodeURIComponent(fileNameStarMatch[1]);
  }

  const fileNameMatch = header.match(/filename="?([^";]+)"?/i);
  if (fileNameMatch?.[1]) {
    return fileNameMatch[1];
  }

  return '';
};

const triggerFileDownload = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const UserManagement = (props: any): React.ReactElement => {
  const { classes, t } = props;
  const [data, setData] = useState<UserRecord[]>([]);
  const [isAddUserDrawerVisible, setIsAddUserDrawerVisible] = useState(false);
  const [isFilterDrawerVisible, setIsFilterDrawerVisible] = useState(false);
  const [isUserDetailsModalVisible, setIsUserDetailsModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState(false);
  const [filters, setFilters] = useState<any>({});
  const [searchText, setSearchText] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isNextPageExists, setIsNextPageExists] = useState(false);

  const loadUsers = async (
    username: string = searchText,
    filterValues: any = filters,
    page = pageNumber,
    size = pageSize,
  ) => {
    setLoading(true);
    const params = buildVendorUserParams(username, filterValues, page, size);
    const response = await fetchVendorUsers(params);
    if (response.isSuccess) {
      const responseData = response.data || {};
      const usersArray = Array.isArray(responseData)
        ? responseData
        : responseData.vendorUsers || responseData.users || [];
      const currentPage = responseData.page ?? page;
      const currentPageSize = responseData.pageSize ?? size;
      const nextExists = responseData.isNextPageExists ?? false;
      const users = usersArray.map((user: any, index: number) => {
        let status = '';
        if (user.is_active !== undefined) {
          status = user.is_active ? 'Active' : 'Inactive';
        } else if (user.status) {
          status = user.status.charAt(0).toUpperCase() + user.status.slice(1);
        }

        const resolvedHubs: HubRef[] = resolveUserHubs(user);

        return {
          key: user.id || String(index),
          id: user.id,
          serialNumber: (currentPage - 1) * currentPageSize + index + 1,
          userCode: user.username,
          name: user.name,
          phoneNumber: user.phone,
          city: user.city || '',
          loginUsername: user.email,
          status,
          creationDate: user.created_at
            ? new Date(user.created_at).toLocaleString()
            : '',
          lastUpdated: user.updated_at
            ? new Date(user.updated_at).toLocaleString()
            : '',
          roles: user.roles?.length ? user.roles : [],
          hubs: resolvedHubs,
          hubIds: resolvedHubs.map((h) => h.id),
        };
      });
      setData(users);
      setIsNextPageExists(nextExists);
      setPageNumber(currentPage);
      setPageSize(currentPageSize);
    } else {
      message.error(response.errorMessage || 'Failed to fetch users');
    }
    setLoading(false);
  };

  const handleDownload = async () => {
    setIsDownloadLoading(true);
    try {
      const params = buildVendorUserParams(searchText, filters, pageNumber, pageSize);
      const response = await downloadVendorUsers(params);
      if (response.isSuccess) {
        const blobData = response.data;
        if (blobData instanceof Blob) {
          const derivedFileName = getFileNameFromContentDisposition(response.fileName);
          const fallbackFileName = `vendor-users-${new Date()
            .toISOString()
            .replace(/[:.]/g, '-')}.csv`;
          const fileName = derivedFileName || fallbackFileName;
          const finalFileName = fileName.toLowerCase().endsWith('.csv')
            ? fileName
            : `${fileName}.csv`;
          triggerFileDownload(blobData, finalFileName);
          message.success(t('Download started'));
        } else {
          message.error(t('Failed to download users'));
        }
      } else {
        const errorMessage =
          typeof response.errorMessage === 'string'
            ? response.errorMessage
            : t('Failed to download users');
        message.error(errorMessage);
      }
    } catch (error) {
      message.error(t('Failed to download users'));
    } finally {
      setIsDownloadLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    {
      title: t('S. No'),
      dataIndex: 'serialNumber',
      key: 'serialNumber',
    },
    {
      title: t('Username'),
      dataIndex: 'userCode',
      key: 'userCode',
      render: (text: string, record: UserRecord) => (
        <button
          type="button"
          style={{ background: 'none', border: 'none', color: '#0780FE', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
          onClick={() => {
            setSelectedUser(record);
            setIsUserDetailsModalVisible(true);
          }}
        >
          {text}
        </button>
      ),
    },
    {
      title: t('Name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('Phone Number'),
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
    },
    {
      title: t('City'),
      dataIndex: 'city',
      key: 'city',
    },
    {
      title: t('Email'),
      dataIndex: 'loginUsername',
      key: 'loginUsername',
    },
    {
      title: t('Roles'),
      key: 'roles',
      render: (_: any, record: UserRecord) => {
        const roles = record.roles || [];
        if (!roles.length) {
          return <span style={{ color: '#999' }}>-</span>;
        }
        const preview = roles.slice(0, 2);
        const overflow = roles.length - preview.length;
        return (
          <>
            {preview.map((r) => (
              <Tag key={r} color="blue">
                {r}
              </Tag>
            ))}
            {overflow > 0 && (
              <Tooltip title={roles.slice(2).join(', ')}>
                <Tag>+{overflow}</Tag>
              </Tooltip>
            )}
          </>
        );
      },
    },
    {
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <span style={{ color: status === 'Active' ? 'green' : 'red' }}>{status}</span>
      ),
    },
    {
      title: t('Creation Date'),
      dataIndex: 'creationDate',
      key: 'creationDate',
    },
    {
      title: t('Last Updated'),
      dataIndex: 'lastUpdated',
      key: 'lastUpdated',
    },
    {
      title: t('Action'),
      key: 'action',
      render: (_: any, record: UserRecord) => (
        <button
          type="button"
          style={{ background: 'none', border: 'none', color: '#0780FE', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
          onClick={() => {
            setSelectedUser(record);
            setIsAddUserDrawerVisible(true);
          }}
        >
          {t('Edit')}
        </button>
      ),
    },
  ];

  return (
    <div className={classes.mainDiv}>
      <Navbar title={t('User Management')} />
      <div className={classes.content}>
        <div className={classes.actionsRow}>
          <div className={classes.searchRow}>
            <Input.Search
              placeholder={t('Search Username')}
              className={classes.searchInput}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={(value) => {
                setSearchText(value);
                const newPage = 1;
                setPageNumber(newPage);
                loadUsers(value, filters, newPage, pageSize);
              }}
            />
            <Button
              type="link"
              icon={<FilterOutlined />}
              onClick={() => setIsFilterDrawerVisible(true)}
            >
              {t('More Filters')}
            </Button>
            <Button
              type="link"
              icon={<CloseCircleOutlined />}
              onClick={() => {
                const newPage = 1;
                setFilters({});
                setSearchText('');
                setPageNumber(newPage);
                loadUsers('', {}, newPage, pageSize);
              }}
            >
              {t('Clear Filters')}
            </Button>
          </div>
          <div>
            <Button
              style={{ marginRight: 8 }}
              loading={isDownloadLoading}
              onClick={handleDownload}
            >
              {t('Download')}
            </Button>
            <Button
              type="primary"
              onClick={() => {
                setSelectedUser(null);
                setIsAddUserDrawerVisible(true);
              }}
            >
              {t('Add User')}
            </Button>
          </div>
        </div>
        <Table columns={columns} dataSource={data} pagination={false} loading={loading} />
        <div className={classes.paginationRow}>
          <SimplePaginator
            loading={loading}
            onFirstClick={() => {
              const newPage = 1;
              setPageNumber(newPage);
              loadUsers(searchText, filters, newPage, pageSize);
            }}
            onPrevClick={() => {
              if (pageNumber > 1) {
                const newPage = pageNumber - 1;
                setPageNumber(newPage);
                loadUsers(searchText, filters, newPage, pageSize);
              }
            }}
            onNextClick={() => {
              if (isNextPageExists) {
                const newPage = pageNumber + 1;
                setPageNumber(newPage);
                loadUsers(searchText, filters, newPage, pageSize);
              }
            }}
            isNext={isNextPageExists}
            pageNumber={pageNumber}
          />
          <Select
            className={classes.selectWithoutBorder}
            onChange={(value) => {
              setPageSize(value);
              const newPage = 1;
              setPageNumber(newPage);
              loadUsers(searchText, filters, newPage, value);
            }}
            value={pageSize}
          >
            <Select.Option value={10}>{t('10/page')}</Select.Option>
            <Select.Option value={20}>{t('20/page')}</Select.Option>
            <Select.Option value={50}>{t('50/page')}</Select.Option>
            <Select.Option value={100}>{t('100/page')}</Select.Option>
          </Select>
        </div>
      </div>
      <AddUserDrawer
        isVisible={isAddUserDrawerVisible}
        handleClose={() => {
          setIsAddUserDrawerVisible(false);
          setSelectedUser(null);
        }}
        t={t}
        user={selectedUser}
        onSuccess={() => loadUsers()}
      />
      <UserDetailsModal
        isVisible={isUserDetailsModalVisible}
        handleClose={() => {
          setIsUserDetailsModalVisible(false);
          setSelectedUser(null);
        }}
        handleEdit={() => {
          setIsUserDetailsModalVisible(false);
          setIsAddUserDrawerVisible(true);
        }}
        t={t}
        user={selectedUser}
      />
      <FilterUsersDrawer
        isVisible={isFilterDrawerVisible}
        handleClose={() => {
          setIsFilterDrawerVisible(false);
        }}
        t={t}
        initialFilters={filters}
        onApply={(values) => {
          setFilters(values);
          const newPage = 1;
          setPageNumber(newPage);
          loadUsers(searchText, values, newPage, pageSize);
        }}
      />
    </div>
  );
};

const hocConfig: HocOptions = {
  connectRedux: { useRedux: false },
  connectJss: { useJss: true, styleSheet: UserManagementStyles },
  connectRouter: true,
  connectTranslession: true,
};

export default GenericHoc(hocConfig)(UserManagement);
